import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getSecretByName } from "../../redux/action/keyVaultAction";

export default function IDWiseKYC({
    flowId,
    user,
    onFinished,
    onCancelled,
    onStarted,
}) {
    const idwiseRef = useRef(null);
    const [instanceKey, setInstanceKey] = useState(0);
    const lang = useSelector((state) => state.translation.lang);

    const kycUserId = user?.user_id || user?.id;

    useEffect(() => {
        let isCancelled = false;

        const wait = (ms) => new Promise((r) => setTimeout(r, ms));

        const hardReset = async () => {
            try {
                if (idwiseRef.current?.cleanup) {
                    await idwiseRef.current.cleanup();
                    idwiseRef.current = null;
                }

                if (window.IDWise?.cleanup) {
                    await window.IDWise.cleanup();
                }

                const el = document.getElementById("idwise-container");
                if (el) el.innerHTML = "";

                setInstanceKey((k) => k + 1);
                await wait(300);
            } catch (e) {
                console.warn("IDWise hard reset failed", e);
            }
        };

        const killSession = async () => {
            try {
                await idwiseRef.current?.cleanup();
                await window.IDWise?.cleanup();
                idwiseRef.current = null;
            } catch {}
        };

        const start = async () => {
            try {
                if (!window.IDWise) throw new Error("IDWise SDK not loaded");

                await hardReset();
                if (isCancelled) return;

                const session = await window.IDWise.initialize({
                    clientKey: process.env.REACT_APP_IS_AZURE_VAULT
                        ? process.env.REACT_APP_IDWISE_API_KEY
                        : await dispatch(
                              getSecretByName("REACT_APP_IDWISE_API_KEY")
                          ),
                    locale: lang === "ar" ? "ar" : "en",
                    theme: "system_default",
                });

                if (isCancelled) return;

                idwiseRef.current = session;

                await session.startJourney({
                    flowId,
                    mount: "#idwise-container",
                    referenceNo: String(kycUserId),
                    displayMode: "embedded",

                    eventHandlers: {
                        // User started journey
                        onJourneyStarted: ({ journeyId }) => {
                            // console.log("Journey started:", journeyId);
                            onStarted?.(String(kycUserId));
                        },

                        // User resumed an existing journey
                        onJourneyResumed: ({ journeyId }) => {
                            // console.log("Journey resumed:", journeyId);
                        },

                        // User successfully completed KYC
                        onJourneyFinished: ({ journeyId }) => {
                            // console.log("Journey finished:", journeyId);
                            onFinished?.(journeyId);
                        },

                        // User clicked Close / Cancel
                        onJourneyCancelled: async ({ journeyId }) => {
                            console.log("Journey cancelled:", journeyId);
                            await killSession();
                            onCancelled?.(journeyId);
                        },

                        // Any blocking error
                        onError: async (err) => {
                            console.error("IDWise error:", err);

                            // User closed or aborted → kill backend session
                            if (
                                err?.code === "USER_EXITED" ||
                                err?.code === "JOURNEY_CANCELLED" ||
                                err?.message?.toLowerCase().includes("cancel")
                            ) {
                                await killSession();
                                onCancelled?.();
                                return;
                            }

                            // Other errors → auto-retry
                            setTimeout(() => {
                                if (!isCancelled) start();
                            }, 1000);
                        },
                    },
                });
            } catch (err) {
                // console.error("IDWise init failed, retrying", err,flowId);
                setTimeout(() => {
                    if (!isCancelled) start();
                }, 1000);
            }
        };

        start();

        // Kill session on browser close / back
        window.addEventListener("beforeunload", killSession);
        window.addEventListener("popstate", killSession);

        return () => {
            isCancelled = true;
            killSession();
            window.removeEventListener("beforeunload", killSession);
            window.removeEventListener("popstate", killSession);
        };
    }, [flowId, kycUserId, lang]);

    return (
        <div className="idwise-wrap">
            <div
                key={instanceKey}
                id="idwise-container"
                className="idwise-container"
            />
        </div>
    );
}
